import { isTransportTRPCError } from '@mybonus/public';
import { useEffect, useMemo, useState, useRef } from 'react';

import { useAPI, useMutation, APIInput, APIOutput, APIError } from '../api';
import { useIsomorphicLayoutEffect } from './isomorphic-layout-effect';
import { usePoll } from './poll';

export type BankIdOpts = {
	onIdentified: () => void;
	handleRedirectToApp: (uri: string) => void;
	isAppActive?: boolean;
	resumePollDelayMs?: number;
};

const DEFAULT_RESUME_POLL_DELAY_MS = 1000;

type ResumePollPhase = 'idle' | 'awaiting-first-poll' | 'retried';

function isTerminalBankIdStatus(
	status: APIOutput['user']['identification']['bankid']['status']['status'] | undefined,
): boolean {
	return status === 'complete' || status === 'failed' || status === 'timeout';
}

export function shouldRetryResumedBankIdStatusPoll(args: {
	error: unknown;
	isFirstPollAfterResume: boolean;
	hasRetriedAfterResume: boolean;
}): boolean {
	return (
		args.isFirstPollAfterResume && !args.hasRetriedAfterResume && isTransportTRPCError(args.error)
	);
}

export function useBankId(opts: BankIdOpts) {
	const { api } = useAPI();
	const isAppActive = opts.isAppActive ?? true;
	const resumePollDelayMs = opts.resumePollDelayMs ?? DEFAULT_RESUME_POLL_DELAY_MS;

	const [flow, setFlow] = useState<'initial' | 'select' | 'external' | 'device'>('initial');

	const init = useMutation<
		APIInput['user']['identification']['bankid']['init'],
		APIOutput['user']['identification']['bankid']['init']
	>(() => api.user.identification.bankid.init.mutate());

	const cancel = useMutation<
		APIInput['user']['identification']['bankid']['cancel'],
		APIOutput['user']['identification']['bankid']['cancel']
	>((args) => api.user.identification.bankid.cancel.mutate(args));

	const [statusData, setStatusData] = useState<
		APIOutput['user']['identification']['bankid']['status'] | undefined
	>();
	const [statusError, setStatusError] = useState<APIError | undefined>();

	const orderRef = useMemo(() => init.data?.orderRef, [init.data]);
	const autoStartToken = useMemo(() => init.data?.autoStartToken, [init.data]);
	const qr = useMemo(() => statusData?.qr, [statusData]);
	const statusState = statusData?.status;

	const activeOrderRef = useRef<string | undefined>(undefined);
	const isAppActiveRef = useRef(isAppActive);
	const previousAppActiveRef = useRef(isAppActive);
	const previousOrderRef = useRef(orderRef);
	const statusRequestGeneration = useRef(0);
	const resumePollPhase = useRef<ResumePollPhase>('idle');
	const onIdentifiedRef = useRef(opts.onIdentified);
	const handleRedirectToAppRef = useRef(opts.handleRedirectToApp);

	const requestStatus = async (currentOrderRef: string, requestGeneration: number) => {
		try {
			const res = await api.user.identification.bankid.status.mutate({ orderRef: currentOrderRef });

			if (
				activeOrderRef.current !== currentOrderRef ||
				statusRequestGeneration.current !== requestGeneration ||
				!isAppActiveRef.current
			) {
				return;
			}

			setStatusData(res);
			setStatusError(undefined);
			resumePollPhase.current = 'idle';

			if (res.status !== 'pending') {
				poll.stop();
			}
		} catch (err) {
			if (
				activeOrderRef.current !== currentOrderRef ||
				statusRequestGeneration.current !== requestGeneration ||
				!isAppActiveRef.current
			) {
				return;
			}

			if (
				shouldRetryResumedBankIdStatusPoll({
					error: err,
					isFirstPollAfterResume: resumePollPhase.current === 'awaiting-first-poll',
					hasRetriedAfterResume: resumePollPhase.current === 'retried',
				})
			) {
				resumePollPhase.current = 'retried';
				return;
			}

			resumePollPhase.current = 'idle';
			setStatusError(err as APIError);
			poll.stop();
		}
	};

	const poll = usePoll(
		async () => {
			const currentOrderRef = activeOrderRef.current;

			if (currentOrderRef) {
				await requestStatus(currentOrderRef, statusRequestGeneration.current);
			}
		},
		{ intervalMs: 2000 },
	);

	const isPending = statusData?.status === 'pending' && !statusError;

	function resetFlow() {
		poll.stop();
		activeOrderRef.current = undefined;
		statusRequestGeneration.current++;
		resumePollPhase.current = 'idle';

		init.reset();
		setStatusData(undefined);
		setStatusError(undefined);
		cancel.reset();

		setFlow('initial');
	}

	function handleInit(flow: 'select' | 'external' | 'device' = 'select') {
		resetFlow();
		setFlow(flow);
	}

	function handleAbort() {
		if (orderRef && isPending) {
			cancel.mutate({ orderRef });
		}

		resetFlow();
	}

	function handleReset() {
		resetFlow();
	}

	useEffect(() => {
		if (statusError || isTerminalBankIdStatus(statusState)) {
			poll.stop();
			setFlow('initial');
		}
	}, [statusState, statusError]);

	useEffect(() => {
		onIdentifiedRef.current = opts.onIdentified;
		handleRedirectToAppRef.current = opts.handleRedirectToApp;
	}, [opts.handleRedirectToApp, opts.onIdentified]);

	useEffect(() => {
		if (flow === 'device' && autoStartToken) {
			handleRedirectToAppRef.current(`bankid:///?autostarttoken=${autoStartToken}&redirect=null`);
		}
	}, [autoStartToken, flow]);

	useIsomorphicLayoutEffect(() => {
		const wasAppActive = previousAppActiveRef.current;
		const previousOrder = previousOrderRef.current;
		const appStateChanged = wasAppActive !== isAppActive;
		const orderChanged = previousOrder !== orderRef;

		previousAppActiveRef.current = isAppActive;
		previousOrderRef.current = orderRef;
		isAppActiveRef.current = isAppActive;
		activeOrderRef.current = orderRef;

		if (appStateChanged || orderChanged) {
			statusRequestGeneration.current++;
		}

		if (!orderRef || isTerminalBankIdStatus(statusState)) {
			poll.stop();
			resumePollPhase.current = 'idle';
			return;
		}

		if (!isAppActive) {
			poll.pause();
			return;
		}

		if (appStateChanged && isAppActive) {
			resumePollPhase.current = 'awaiting-first-poll';
			poll.start({ initialDelayMs: resumePollDelayMs });
			return;
		}

		if (orderChanged) {
			resumePollPhase.current = 'idle';
			poll.start();
		}
	}, [isAppActive, orderRef, resumePollDelayMs, statusState]);

	useEffect(() => {
		return () => {
			activeOrderRef.current = undefined;
			isAppActiveRef.current = false;
			statusRequestGeneration.current++;
		};
	}, []);

	useEffect(() => {
		let timerId: ReturnType<typeof setTimeout> | undefined;

		if (!statusError && statusState === 'complete' && isAppActive) {
			timerId = setTimeout(() => {
				if (isAppActiveRef.current && activeOrderRef.current === orderRef) {
					onIdentifiedRef.current();
				}
			}, 2000);
		}

		return () => {
			clearTimeout(timerId);
		};
	}, [isAppActive, orderRef, statusError, statusState]);

	useEffect(() => {
		if (flow === 'external' || flow === 'device') {
			init.mutate();
		}
	}, [flow]);

	const isComplete = statusState === 'complete';

	const error =
		init.error ||
		statusError ||
		(statusState === 'failed' && 'Identifieringen misslyckades, försök igen') ||
		cancel.error ||
		(statusState === 'timeout' && 'Det tog lite för lång tid, försök igen');

	return {
		isPending,
		isComplete,
		handleAbort,
		handleReset,
		handleInit,
		error,
		flow,
		setFlow,
		qr,
		autoStartToken,
	};
}
